/**
 * Runtime detection: browser vs Tauri shell.
 * Safe for SSR: only checks window when available.
 */

import type { PlatformType, RuntimeType } from './types'

declare global {
  interface Window {
    __TAURI__?: unknown
  }
}

export function getRuntime(): RuntimeType {
  if (typeof window === 'undefined') return 'browser'
  return '__TAURI__' in window ? 'tauri' : 'browser'
}

export function isInTauriShell(): boolean {
  return getRuntime() === 'tauri'
}

/**
 * Returns platform when in Tauri; otherwise derives from navigator (best-effort).
 */
export function getPlatformFallback(): PlatformType {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent.toLowerCase()
  // Mobile platforms MUST be checked before desktop: iOS Safari reports
  // "like Mac OS X" (so a naive `mac` check misfires to macos) and Android
  // reports "Linux". Check the most specific tokens first.
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'ios'
  if (ua.includes('android')) return 'android'
  if (ua.includes('win')) return 'windows'
  if (ua.includes('mac')) return 'macos'
  if (ua.includes('linux')) return 'linux'
  return 'unknown'
}

/**
 * Returns true when running on a mobile/touch platform (Android or iOS).
 * Use this to gate UI layout decisions — not capability checks (use getCapabilities() for those).
 */
export function isMobilePlatform(): boolean {
  const platform = getPlatformFallback()
  return platform === 'android' || platform === 'ios'
}

/**
 * Returns true on iPadOS 13+, which no UA string will tell you.
 *
 * Since iPadOS 13 Safari sends the DESKTOP Mac user agent ("Macintosh; Intel Mac
 * OS X"), and Chrome on iPad (CriOS) does the same — there is no `ipad` token to
 * match, so {@link getPlatformFallback} answers `macos` and
 * {@link isMobilePlatform} answers false. The one signal Apple left standing is
 * touch: no Mac reports more than 0 touch points, every iPad reports 5.
 *
 * Deliberately NOT folded into `getPlatformFallback`/`isMobilePlatform`: those
 * drive camera selection (facing mode, mirroring) and list-vs-grid interaction,
 * and flipping an iPad from "macos" to "ios" would change all of that in one go.
 * This is for layout questions only — today, whether safe-area insets exist.
 */
export function isIpadOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent?.toLowerCase() ?? ''
  if (ua.includes('ipad')) return true
  return ua.includes('macintosh') && (navigator.maxTouchPoints ?? 0) > 1
}

/**
 * Returns true when running on a tablet-class device.
 * Heuristic: mobile platform + screen short-side >= 600 logical pixels.
 */
export function isTabletPlatform(): boolean {
  if (!isMobilePlatform()) return false
  if (typeof window === 'undefined') return false
  const shortSide = Math.min(window.screen.width, window.screen.height)
  return shortSide >= 600
}
